import { useState } from 'react'
import { useTranslation } from '../../i18n'
import { replayEventCounts } from '../../lib/replayEvents'
import { COLORS } from '../../lib/theme'
import type { ReplayBundle, ReplayEventFilterKey, ReplayParticipant } from '../../lib/replayTypes'
import { Icon } from '../Icon'
import { BarkingDogIcon, DogFacePin, MemberDot, ReplayCheckMark, replayPinSrc } from './replayIcons'
import { useReplayParticipantName } from './replayParticipantName'

export interface ReplayFilterControlsProps {
  bundle: ReplayBundle
  hiddenParticipants: ReadonlySet<string>
  hiddenEvents: ReadonlySet<ReplayEventFilterKey>
  onToggleParticipant: (id: string) => void
  onToggleEvent: (key: ReplayEventFilterKey) => void
  onSetRoleVisibility: (role: ReplayParticipant['role'], visible: boolean) => void
  onSetAllEventsVisible: (visible: boolean) => void
}

const EVENT_ORDER: ReplayEventFilterKey[] = [
  'observed',
  'downed',
  'wounded',
  'barking',
  'messages',
]

type OpenControl = 'members' | 'dogs' | 'events' | null

/**
 * Team members, dogs and event types — three standalone controls at the top-left of
 * the map, each a pill that expands in place into its own selection card.
 *
 * One open at a time: they overlay the terrain, and two open cards would cover the
 * hunt you opened them to look at.
 *
 * Filters are stored as the set of things HIDDEN rather than shown, so a hunt that
 * loads with new participants has them visible by default instead of silently
 * filtered out.
 */
export function ReplayFilterControls({
  bundle,
  hiddenParticipants,
  hiddenEvents,
  onToggleParticipant,
  onToggleEvent,
  onSetRoleVisibility,
  onSetAllEventsVisible,
}: ReplayFilterControlsProps) {
  const { t } = useTranslation('replay')
  const nameOf = useReplayParticipantName()
  const [open, setOpen] = useState<OpenControl>(null)
  const toggleOpen = (key: Exclude<OpenControl, null>) => () =>
    setOpen((current) => (current === key ? null : key))

  const counts = replayEventCounts(bundle)
  const hunters = bundle.participants.filter((p) => p.role === 'hunter')
  const dogs = bundle.participants.filter((p) => p.role === 'dog')
  const visibleOf = (group: ReplayParticipant[]) =>
    group.filter((p) => !hiddenParticipants.has(p.id)).length

  const eventsOn = EVENT_ORDER.filter((key) => !hiddenEvents.has(key)).length
  const allEventsOn = eventsOn === EVENT_ORDER.length

  const divider = (
    <div style={{ borderTop: `1px solid ${COLORS.outlineVariant}`, margin: '2px 0' }} />
  )

  return (
    <div
      data-testid="replay-filter-controls"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}
    >
      {hunters.length > 0 && (
        <FilterControl
          icon={<Icon name="group" size={18} />}
          label={t('filters.members')}
          detail={t('filters.count', { selected: visibleOf(hunters), total: hunters.length })}
          open={open === 'members'}
          onToggleOpen={toggleOpen('members')}
        >
          <FilterRow
            bold
            label={t('filters.selectAllMembers')}
            checked={visibleOf(hunters) === hunters.length}
            onToggle={() => onSetRoleVisibility('hunter', visibleOf(hunters) !== hunters.length)}
          />
          {divider}
          {hunters.map((participant) => (
            <FilterRow
              key={participant.id}
              label={
                participant.isMe === true && participant.anonymous !== true
                  ? t('filters.meSuffix', { name: participant.name })
                  : nameOf(participant)
              }
              secondary={participant.anonymous === true ? t('removedTrail') : undefined}
              checked={!hiddenParticipants.has(participant.id)}
              onToggle={() => onToggleParticipant(participant.id)}
              // No initial for an anonymous person: even a letter narrows down who it was.
              icon={
                <MemberDot
                  name={participant.anonymous === true ? '' : participant.name}
                  color={participant.color}
                />
              }
            />
          ))}
        </FilterControl>
      )}

      {dogs.length > 0 && (
        <FilterControl
          icon={<Icon name="dog" size={18} />}
          label={t('filters.dogs')}
          detail={t('filters.count', { selected: visibleOf(dogs), total: dogs.length })}
          open={open === 'dogs'}
          onToggleOpen={toggleOpen('dogs')}
        >
          <FilterRow
            bold
            label={t('filters.allDogs')}
            checked={visibleOf(dogs) === dogs.length}
            onToggle={() => onSetRoleVisibility('dog', visibleOf(dogs) !== dogs.length)}
          />
          {divider}
          {dogs.map((participant) => (
            <FilterRow
              key={participant.id}
              label={nameOf(participant)}
              secondary={participant.anonymous === true ? t('removedTrail') : undefined}
              checked={!hiddenParticipants.has(participant.id)}
              onToggle={() => onToggleParticipant(participant.id)}
              icon={<DogFacePin color={participant.color} size={20} />}
            />
          ))}
        </FilterControl>
      )}

      <FilterControl
        icon={<Icon name="pin" size={18} />}
        label={t('filters.events')}
        detail={t('filters.count', { selected: eventsOn, total: EVENT_ORDER.length })}
        open={open === 'events'}
        onToggleOpen={toggleOpen('events')}
      >
        <FilterRow
          bold
          label={t('filters.allEvents')}
          checked={allEventsOn}
          onToggle={() => onSetAllEventsVisible(!allEventsOn)}
        />
        {divider}
        {EVENT_ORDER.map((key) => (
          <FilterRow
            key={key}
            label={`${t(`events.${key}`)} (${counts[key]})`}
            checked={!hiddenEvents.has(key)}
            onToggle={() => onToggleEvent(key)}
            icon={eventIcon(key)}
          />
        ))}
      </FilterControl>
    </div>
  )
}

function eventIcon(key: ReplayEventFilterKey): React.ReactNode {
  switch (key) {
    case 'observed':
      return <img src={replayPinSrc('observed')} alt="" width={18} height={19} style={{ display: 'block' }} />
    case 'downed':
    case 'wounded':
      return <img src={replayPinSrc('downed')} alt="" width={18} height={19} style={{ display: 'block' }} />
    case 'barking':
      // Still, not pulsing: an animated icon in a list is noise.
      return <BarkingDogIcon size={22} animated={false} color={COLORS.onSurface} />
    case 'messages':
      return <Icon name="chat" size={18} />
  }
}

/** A pill header — icon, label, count, chevron — that expands into a selection card. */
function FilterControl({
  icon,
  label,
  detail,
  open,
  onToggleOpen,
  children,
}: {
  icon: React.ReactNode
  label: string
  detail: string
  open: boolean
  onToggleOpen: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className="wh-card"
      style={{
        padding: open ? '4px 14px 10px' : 0,
        width: open ? 264 : 'auto',
        maxWidth: 'calc(100vw - 24px)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          border: 'none',
          background: 'transparent',
          padding: open ? '8px 0' : '9px 14px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          color: COLORS.onSurface,
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'inline-flex', width: 22, justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </span>
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 400, color: COLORS.outline }}>{detail}</span>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={17} color={COLORS.outline} />
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '42vh', overflowY: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * A row with a leading icon, a label, and the app's selection checkmark, and an
 * optional second line under the label.
 */
function FilterRow({
  label,
  secondary,
  checked,
  onToggle,
  icon,
  bold,
}: {
  label: string
  secondary?: string
  checked: boolean
  onToggle: () => void
  icon?: React.ReactNode
  bold?: boolean
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 4px',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: bold === true ? 600 : 400,
        color: COLORS.onSurface,
      }}
    >
      {/* The native input carries the role, the label and the keyboard behaviour;
          ReplayCheckMark is only its appearance, so the input is hidden rather than
          replaced. */}
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        // Visually hidden, not removed: the input keeps the role, the label
        // association and keyboard operation, and ReplayCheckMark is only its
        // appearance. It stays pointer-clickable — it sits inside the label, so a
        // direct click on it toggles exactly as clicking the row does, and making it
        // unclickable only puts the control out of reach of automation.
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, margin: 0 }}
      />
      {icon !== undefined && (
        <span style={{ display: 'inline-flex', width: 26, justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {secondary !== undefined && (
          <span
            style={{
              fontSize: 12,
              color: COLORS.outline,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {secondary}
          </span>
        )}
      </span>
      <ReplayCheckMark checked={checked} />
    </label>
  )
}
